import dispatchChartService from "api/dispatchChartService";
import {
  EParcelDeliveryCategory,
  IParcelEvidence,
} from "dto/Mailroom/IDispatchChart";
import { ENotificationType, notification } from "utils/notification";

/** Etykieta skanu potwierdzenia wg formy doręczenia (Magistrat: ParcelType 2/4/11). */
export const receiptLabelKey = (category?: number | null) => {
  switch (category) {
    case EParcelDeliveryCategory.PersonalPickup:
      return "metric.delivery.previewPickup";
    case EParcelDeliveryCategory.PersonalDelivery:
      return "metric.delivery.previewPersonalDelivery";
    default:
      return "metric.delivery.previewReceipt";
  }
};

export interface EvidenceBlob {
  blob: Blob;
  fileName: string;
  contentType: string;
  /** PDF → podgląd w modalu; XML i inne → od razu pobranie. */
  previewable: boolean;
}

/** Dowód zapisany jako XML — obok podglądu (wizualizacja PDF) dostaje akcję „Pobierz oryginał". */
export const isXmlEvidence = (evidence: IParcelEvidence) =>
  /\.xml$/i.test(evidence.FileName ?? "");

/**
 * Pobiera jawny plik dowodu (UPP/UPD/UPO/EPO) przesyłki wychodzącej. Zwraca null, gdy
 * pobranie się nie powiodło (błąd już pokazany użytkownikowi).
 * `asPdf` (domyślnie) — dowód XML przychodzi jako wizualizacja PDF do podglądu; `false` —
 * oryginalny, podpisany plik (to on ma moc dowodową).
 */
export const fetchParcelEvidence = async (
  evidence: IParcelEvidence,
  asPdf = true
): Promise<EvidenceBlob | null> => {
  try {
    const res = await dispatchChartService.getParcelEvidenceFile(
      evidence.Id,
      asPdf
    );
    const blob = res.data as Blob;
    const contentType = blob.type || "";
    const isPdf = contentType.includes("pdf");
    const fallbackExt = isPdf ? "pdf" : "xml";
    const baseName =
      evidence.FileName ??
      `dowod_${(evidence.Type ?? evidence.Source).replace(
        /[^\w.-]/g,
        "_"
      )}.${fallbackExt}`;
    const fileName = isPdf ? baseName.replace(/\.xml$/i, ".pdf") : baseName;
    const previewable = contentType.includes("pdf") || /\.pdf$/i.test(fileName);
    return { blob, fileName, contentType, previewable };
  } catch (e) {
    notification({
      type: ENotificationType.ERROR,
      title: "Błąd",
      text: (e as Error)?.message ?? "Nie udało się pobrać dowodu.",
    });
    return null;
  }
};

/** Pobiera oryginalny plik dowodu (bez wizualizacji) i zapisuje go na dysku. */
export const downloadParcelEvidenceOriginal = async (
  evidence: IParcelEvidence
) => {
  const file = await fetchParcelEvidence(evidence, false);
  if (file) saveBlob(file.blob, file.fileName);
};

export const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
